// The stored form of a finished online match: what the host writes when a match
// ends and what a replay viewer reads back.
//
// A match's rounds already exist in replay form (match.js keeps each finished
// round as extractReplayGameState: the event logs plus the worm path segments).
// A file is those rounds with every segment in the WIRE form (game-state-functions
// encodeWormPathSegment: the inputs that rebuild it, one-letter keys, rounded),
// which is what makes a full 25-player match about 2MB of JSON and ~270KB
// gzipped instead of 8MB / 1MB. Decoding rebuilds each segment with the same
// createTrajectory the live stream uses, so a replay draws what the players saw.
//
// Beside the rounds a file carries:
//   - meta: which match this was (id, room kind, box, map, times, final score);
//   - matchConfig: the players as seated (names, colours, cosmetics) and the map;
//   - deathReports: what a CLIENT saw when its own worm died (see the client's
//     death-report.ts), per round. The rounds are the host's truth; a report is
//     the other half of a disputed death ("I died in the open").
//
// The file holds EVERYTHING, bots named as bots included: it is an internal
// record. Whoever serves it to a player strips it first (publicReplayFile).
import * as gsf from "./game-state-functions.js";

export var REPLAY_FILE_VERSION = 1;

function mapTracks(tracks, fn) {
    var out = {};
    Object.keys(tracks || {}).forEach(function (trackId) {
        out[trackId] = (tracks[trackId] || []).map(fn);
    });
    return out;
}

function encodeRound(roundData) {
    var gs = roundData.gameState;
    var state = {};
    Object.keys(gs).forEach(function (key) { state[key] = gs[key]; });
    state.wormPathSegments = mapTracks(gs.wormPathSegments, gsf.encodeWormPathSegment);
    return {
        startScore: roundData.startScore,
        roundScore: roundData.roundScore,
        endScore: roundData.endScore,
        gameState: state
    };
}

function decodeRound(round) {
    var state = {};
    Object.keys(round.gameState).forEach(function (key) { state[key] = round.gameState[key]; });
    state.wormPathSegments = mapTracks(round.gameState.wormPathSegments, gsf.decodeWormPathSegment);
    return {
        startScore: round.startScore,
        roundScore: round.roundScore,
        endScore: round.endScore,
        gameState: state
    };
}

// meta: { matchId, kind, base, playstyle, mapName, startedAt, endedAt, score, left }
// roundsData: match.matchState.roundsData. deathReports: [{ round, playerId, ... }].
export function encodeReplayFile(options) {
    return {
        v: REPLAY_FILE_VERSION,
        meta: options.meta || {},
        matchConfig: options.matchConfig,
        rounds: (options.roundsData || []).map(encodeRound),
        deathReports: options.deathReports || []
    };
}

// Back to what the replay player takes: a match-shaped object
// ({ matchConfig, matchState: { roundsData } }), plus the meta and the reports.
// Returns null for anything that is not a replay file of a version we know.
export function decodeReplayFile(file) {
    if (!file || file.v !== REPLAY_FILE_VERSION || !Array.isArray(file.rounds) || !file.matchConfig) {
        return null;
    }
    return {
        meta: file.meta || {},
        match: {
            matchConfig: file.matchConfig,
            matchState: { roundsData: file.rounds.map(decodeRound) }
        },
        deathReports: file.deathReports || []
    };
}

// The short line a list of replays shows, kept beside the file (object metadata)
// so listing never has to open a file.
export function replaySummary(file) {
    var meta = file.meta || {};
    var players = (file.matchConfig && file.matchConfig.players) || [];
    return {
        matchId: meta.matchId || null,
        kind: meta.kind || null,
        base: meta.base || null,
        mapName: meta.mapName || null,
        startedAt: meta.startedAt || null,
        endedAt: meta.endedAt || null,
        rounds: (file.rounds || []).length,
        players: players.length,
        humans: players.filter(function (p) { return p.type === "human"; }).map(function (p) { return p.name; }),
        deathReports: (file.deathReports || []).length
    };
}
